import { useEffect, useState, useRef } from "react";
import { ChevronDown, Plus, Check, Building2, X, Loader2, Crown, Settings as SettingsIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";

const ROLE_BADGE = {
  admin: { color: "#378ADD", label_key: "org_switcher.role_admin" },
  contributeur: { color: "#1D9E75", label_key: "org_switcher.role_contributeur" },
  lecteur: { color: "#A1A1AA", label_key: "org_switcher.role_lecteur" },
};

const ORG_TYPE_BADGE = {
  projet: { label: "Projet", color: "#EF9F27" },
  departement: { label: "Département", color: "#7F77DD" },
  filiale: { label: "Filiale", color: "#1D9E75" },
};

// Parents d'abord, chaque enfant juste sous son parent
function sortWithChildren(orgs) {
  const roots = orgs.filter((o) => !o.parent_org_id);
  const byParent = {};
  orgs.filter((o) => o.parent_org_id).forEach((o) => {
    (byParent[o.parent_org_id] = byParent[o.parent_org_id] || []).push(o);
  });
  const out = [];
  roots.forEach((r) => { out.push(r); (byParent[r.id] || []).forEach((c) => out.push(c)); });
  // Orphelins (parent non visible pour cet utilisateur)
  orgs.forEach((o) => { if (!out.includes(o)) out.push(o); });
  return out;
}

export default function OrgSwitcher({ activeOrgId, activeOrgName, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchor, setAnchor] = useState({ top: 56, left: 16 });
  const ref = useRef(null);
  const menuRef = useRef(null);

  const load = () =>
    api.get("/workspace/organisations").then((r) => {
      setOrgs(r.data.organisations || []);
      setIsSuperAdmin(!!r.data.is_super_admin);
    }).catch(() => {});

  useEffect(() => {
    load();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      } else if (ref.current && !ref.current.contains(e.target) && !menuRef.current) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = async (id) => {
    if (id === activeOrgId) { setOpen(false); return; }
    setLoading(true);
    try {
      await api.post(`/workspace/organisations/${id}/select`);
      setOpen(false);
      onChanged?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="org-switcher">
      <button
        onClick={(e) => {
          if (!open) {
            const r = e.currentTarget.getBoundingClientRect();
            setAnchor({ top: Math.round(r.bottom + 8), left: Math.round(r.left) });
          }
          setOpen((o) => !o);
        }}
        className="flex items-center gap-1.5 max-w-[220px] hover:text-[#378ADD] transition-colors"
        data-testid="org-switcher-btn"
      >
        <span className="text-white truncate" data-testid="topbar-org-name">{activeOrgName || t("settings.name_placeholder")}</span>
        <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="noam-card py-1.5 min-w-[280px]"
          style={{ position: "fixed", top: anchor.top, left: anchor.left, zIndex: 9999 }}
          data-testid="org-switcher-menu"
        >
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              {t("org_switcher.title")}
            </div>
            {isSuperAdmin && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#EF9F27] font-semibold">
                <Crown className="w-2.5 h-2.5" /> {t("org_switcher.super_admin")}
              </span>
            )}
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {orgs.length === 0 && (
              <div className="px-3 py-3 text-xs text-zinc-500">{t("org_switcher.no_orgs")}</div>
            )}
            {sortWithChildren(orgs).map((o) => {
              const isActive = o.id === activeOrgId;
              const role = ROLE_BADGE[o.role] || ROLE_BADGE.lecteur;
              const typeBadge = ORG_TYPE_BADGE[o.org_type];
              const isChild = !!o.parent_org_id;
              return (
                <button
                  key={o.id}
                  onClick={() => select(o.id)}
                  disabled={loading}
                  className={`w-full text-left px-3 py-2 hover:bg-white/5 disabled:opacity-50 ${isActive ? "bg-[#378ADD]/10" : ""}`}
                  data-testid={`org-switcher-item-${o.slug}`}
                >
                  <div className={`flex items-center gap-2 ${isChild ? "pl-5" : ""}`}>
                    {isChild ? (
                      <span className="text-zinc-600 text-xs shrink-0">└</span>
                    ) : (
                      <Building2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate flex items-center gap-1.5 ${isActive ? "text-[#378ADD]" : "text-white"}`}>
                        {o.name}
                        {typeBadge && (
                          <span className="px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider font-bold shrink-0"
                            style={{ color: typeBadge.color, background: `${typeBadge.color}18`, border: `1px solid ${typeBadge.color}40` }}>
                            {typeBadge.label}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        <span style={{ color: role.color }}>{t(role.label_key)}</span>
                        {o.sector && <><span className="opacity-30">·</span> <span>{o.sector}</span></>}
                      </div>
                    </div>
                    {isActive && <Check className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="border-t border-white/5">
            <Link
              to="/app/settings"
              onClick={() => setOpen(false)}
              className="w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white flex items-center gap-2"
              data-testid="org-switcher-rename"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> {t("org_switcher.rename_settings")}
            </Link>
            <button
              onClick={() => { setOpen(false); setShowCreate(true); }}
              className="w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 hover:text-white flex items-center gap-2"
              data-testid="org-switcher-create"
            >
              <Plus className="w-3.5 h-3.5" /> {t("org_switcher.create_new")}
            </button>
          </div>
        </div>
      )}
      {showCreate && (
        <CreateOrgModal
          parentCandidates={orgs.filter((o) => !o.parent_org_id && (isSuperAdmin || o.role === "admin"))}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated, parentCandidates = [] }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [parentId, setParentId] = useState("");
  const [orgType, setOrgType] = useState("projet");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/workspace/organisations", {
        name: name.trim(),
        sector: sector.trim() || null,
        parent_org_id: parentId || null,
        org_type: parentId ? orgType : "organisation",
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || t("org_switcher.create_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="create-org-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="noam-overline">{t("org_switcher.create_title")}</div>
            <div className="noam-display text-lg font-semibold mt-1">{t("org_switcher.create_subtitle")}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{t("settings.name")}</label>
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("settings.name_placeholder")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="create-org-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{t("settings.sector")}</label>
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="create-org-sector-select"
            >
              <option value="">—</option>
              {["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"].map((s) => (
                <option key={s} value={s}>{t(`settings.values.sector.${s}`, s)}</option>
              ))}
            </select>
          </div>
          {parentCandidates.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Rattacher à (optionnel)</label>
                <select
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="create-org-parent-select"
                >
                  <option value="">— Organisation indépendante —</option>
                  {parentCandidates.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              {parentId && (
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Type de sous-entité</label>
                  <select
                    value={orgType}
                    onChange={(e) => setOrgType(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                    data-testid="create-org-type-select"
                  >
                    <option value="projet">Projet</option>
                    <option value="departement">Département</option>
                    <option value="filiale">Filiale</option>
                  </select>
                </div>
              )}
            </div>
          )}
          {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
          <p className="text-[11px] text-zinc-500">{t("org_switcher.create_help")}</p>
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="create-org-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}</> : t("org_switcher.create_btn")}
          </button>
        </form>
    </Modal>
  );
}
